import React from "react";
import { useLocation } from "react-router-dom";
import {
  getRouteParent,
  translateRoute,
} from "../../helpers/routeTranslate";
import TextLink from "../TextLink";
import style from "./style.module.scss";

interface BreadcrumbItem {
  label: string;
  path: string;
}

export interface BreadcrumbNewProps {
  className?: string;
  separator?: React.ReactNode;
}

function ChevronRightIcon() {
  return (
    <span
      aria-hidden="true"
      className={`material-symbols-sharp ${style.breadcrumbChevron}`}
    >
      chevron_right
    </span>
  );
}

export default function BreadcrumbNew({
  className = "",
  separator = <ChevronRightIcon />,
}: BreadcrumbNewProps) {
  const { pathname } = useLocation();
  const pathSegments = pathname.split("/").filter(Boolean);

  if (pathSegments.length === 0) {
    return null;
  }

  const routeItems = pathSegments.reduce<BreadcrumbItem[]>((items, segment, index) => {
    const label =
      translateRoute(pathSegments.slice(0, index + 1).join("/")) || translateRoute(segment);
    const parent = getRouteParent(segment);

    if (label) {
      if (parent && !items.some((item) => item.path === parent.path)) {
        items.push({
          label: translateRoute(parent.key) || parent.key,
          path: parent.path,
        });
      }

      items.push({
        label,
        path: `/${pathSegments.slice(0, index + 1).join("/")}`,
      });
    }

    return items;
  }, []);

  if (pathname !== "/" && routeItems.length === 0) {
    routeItems.push({
      label: translateRoute("pagina_nao_encontrada") || "Página não encontrada",
      path: pathname,
    });
  }

  const items: BreadcrumbItem[] = [
    { label: translateRoute("home") || "Início", path: "/" },
    ...routeItems,
  ];
  const breadcrumbClassName = [style.breadcrumb, className]
    .filter(Boolean)
    .join(" ");

  return (
    <nav
      aria-label="Navegação estrutural"
      className={breadcrumbClassName}
    >
      <ol className={style.breadcrumbList}>
        {items.map((item, index) => {
          const isCurrentPage = index === items.length - 1;
          const content = index === 0 ? (
            <span
              aria-label="Início"
              className={`material-symbols-sharp ${style.breadcrumbHomeIcon}`}
              role="img"
            >
              home
            </span>
          ) : item.label;
          const linkClassName = [
            "pgm-body-medium",
            "pgm-body-medium--emphasized",
            style.breadcrumbLink,
            index === 0 ? style.breadcrumbHomeLink : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <React.Fragment key={item.path}>
              {index > 0 && (
                <li aria-hidden="true" className={style.breadcrumbSeparator}>
                  {separator}
                </li>
              )}
              <li className={style.breadcrumbItem}>
                {isCurrentPage ? (
                  <span
                    aria-current="page"
                    className={`pgm-body-medium ${style.breadcrumbCurrent}`}
                  >
                    {content}
                  </span>
                ) : (
                  <TextLink
                    className={linkClassName}
                    link={item.path}
                    text={content}
                  />
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
